import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { usePartnrConfirmStore } from '@/stores/partnrConfirmStore';
import { useExtensionsStore } from '@/stores/extensionsStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { getExtension } from './registry';
import { readFunctionError } from './jobs';

/**
 * Run an extension action Partnr prepared (a `run_extension` canvas action).
 * partnr-chat only validates; the run happens here, as the signed-in user, so
 * the server-side enable check and daily cap apply exactly as for a click.
 * Paid actions wait for the user to confirm.
 */
export async function runExtensionFromPartnr(payload: Record<string, unknown>): Promise<void> {
  const extensionId = String(payload.extension_id ?? '');
  const actionId = String(payload.action_id ?? '');
  const entry = getExtension(extensionId);
  const action = entry?.manifest.actions.find((a) => a.id === actionId);
  if (!entry || !action) {
    toast.error('Partnr asked for an extension this app does not have.');
    return;
  }
  const { manifest } = entry;

  const extensions = useExtensionsStore.getState();
  if (!extensions.loaded) await extensions.load();
  if (!useExtensionsStore.getState().isEnabled(extensionId)) {
    toast.error(`${manifest.name} is turned off. Turn it on in Extensions to use it.`);
    return;
  }

  // Use the manifest's function, never one named in the payload.
  const given = (payload.input as Record<string, unknown>) ?? {};
  // Actions that place results go to the open board unless Partnr named one
  const openBoardId = useCanvasStore.getState().currentBoardId;
  const input = action.input.properties?.board_id && !given.board_id && openBoardId ? { ...given, board_id: openBoardId } : given;
  // Partnr 2 already asked inline (toolApproval) before the server prepared this run
  if (action.cost === 'paid' && payload.approved !== true) {
    const runsLeft = typeof payload.runs_left === 'number' ? payload.runs_left : null;
    const confirmed = await usePartnrConfirmStore.getState().ask({
      title: `Run ${manifest.name}?`,
      description:
        `Partnr wants to ${action.label.toLowerCase()} with ${manifest.name}. This uses paid scraping credits` +
        (runsLeft !== null ? ` and 1 of your ${runsLeft} runs left today.` : '.'),
      items: Array.isArray(payload.details) ? (payload.details as unknown[]).map(String) : [],
      confirmLabel: 'Run',
      destructive: false,
    });
    if (!confirmed) {
      toast.info(`${manifest.name} was not run`);
      return;
    }
  }

  const loadingId = toast.loading(`Running ${manifest.name}...`);
  const { data, error } = await supabase.functions.invoke(action.function, { body: input });
  toast.dismiss(loadingId);
  if (error) {
    const { message } = await readFunctionError(error);
    toast.error(`${manifest.name}: ${message}`);
    return;
  }

  if (entry.onRunStarted) entry.onRunStarted((data ?? {}) as Record<string, unknown>);
  else toast.success(`${manifest.name} is done.`);
}
